import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Stage, Layer, Image as KonvaImage, Text, Rect, Circle, Line, Group, Transformer } from 'react-konva';
import Konva from 'konva';

// Custom hook to load images
const FILTER_OPTIONS = [
  { id: 'blur', label: 'Blur', cssFilter: 'blur(3px)', konva: (img: Konva.Image) => img.blurRadius(8) },
  { id: 'brighten', label: 'Bright', cssFilter: 'brightness(1.35)', konva: (img: Konva.Image) => img.brightness(0.2) },
  { id: 'contrast', label: 'Contrast', cssFilter: 'contrast(1.2)', konva: (img: Konva.Image) => img.contrast(0.2) },
  { id: 'grayscale', label: 'Mono', cssFilter: 'grayscale(1)' },
  { id: 'invert', label: 'Invert', cssFilter: 'invert(1)' },
  { id: 'sepia', label: 'Sepia', cssFilter: 'sepia(1)' },
  { id: 'sharpen', label: 'Sharp', cssFilter: 'contrast(1.2) saturate(1.2)' },
  { id: 'vintage', label: 'Vintage', cssFilter: 'sepia(0.6) saturate(1.2) contrast(1.1)' },
  { id: 'polaroid', label: 'Polaroid', cssFilter: 'contrast(1.1) saturate(1.3) hue-rotate(-5deg)' },
  { id: 'kodachrome', label: 'Kodachrome', cssFilter: 'contrast(1.4) saturate(1.5) hue-rotate(-10deg)' },
  { id: 'technicolor', label: 'Technicolor', cssFilter: 'saturate(1.6) hue-rotate(10deg)' },
  { id: 'brownie', label: 'Brownie', cssFilter: 'sepia(0.9) contrast(1.1)' },
  { id: 'comic', label: 'Comic', cssFilter: 'contrast(1.5) saturate(2)' },
  { id: 'cool', label: 'Cool', cssFilter: 'brightness(1.05) saturate(1.2) hue-rotate(20deg)' },
  { id: 'warm', label: 'Warm', cssFilter: 'brightness(1.05) saturate(1.2) hue-rotate(-15deg)' },
  { id: 'dream', label: 'Dream', cssFilter: 'brightness(1.1) saturate(1.3) blur(1px)' },
];

const DEFAULT_PREVIEW_IMAGE =
  'https://images.unsplash.com/photo-1503023345310-bd7c1de61c7d?auto=format&fit=crop&w=300&q=60';

const useImage = (url: string | null | undefined): [HTMLImageElement | undefined, 'loading' | 'loaded' | 'error'] => {
  const [image, setImage] = useState<HTMLImageElement | undefined>(undefined);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  useEffect(() => {
    if (!url) {
      setImage(undefined);
      setStatus('error');
      return;
    }

    setStatus('loading');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImage(img);
      setStatus('loaded');
    };
    img.onerror = () => {
      setImage(undefined);
      setStatus('error');
    };
    img.src = url;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [url]);

  return [image, status];
};

interface KonvaImageEditorProps {
  isOpen: boolean;
  initialImage?: string | null;
  onClose: () => void;
  onSave: (dataUrl: string) => void;
  isSaving?: boolean;
}

type EditorMode =
  | 'none'
  | 'filter'
  | 'resize'
  | 'crop'
  | 'draw'
  | 'text'
  | 'shapes'
  | 'stickers'
  | 'frame'
  | 'corners'
  | 'merge';

interface TextElement {
  id: string;
  x: number;
  y: number;
  text: string;
  fontSize: number;
  fontFamily: string;
  fill: string;
  fontStyle?: string;
  fontWeight?: string;
}

interface ShapeElement {
  id: string;
  type: 'rect' | 'circle' | 'line';
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
}

interface DrawPath {
  id: string;
  points: number[];
  stroke: string;
  strokeWidth: number;
}

const KonvaImageEditor: React.FC<KonvaImageEditorProps> = ({
  isOpen,
  initialImage,
  onClose,
  onSave,
  isSaving = false,
}) => {
  const stageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const groupRef = useRef<Konva.Group>(null);
  const [image] = useImage(initialImage || '');
  const [mode, setMode] = useState<EditorMode>('filter');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [textElements, setTextElements] = useState<TextElement[]>([]);
  const [shapeElements, setShapeElements] = useState<ShapeElement[]>([]);
  const [drawPaths, setDrawPaths] = useState<DrawPath[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState<number[]>([]);
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0, scale: 1 });
  const [zoom, setZoom] = useState(100);
  const [filters, setFilters] = useState<string[]>([]);
  const imageRef = useRef<Konva.Image>(null);
  const [textColor, setTextColor] = useState('#000000');
  const [shapeColor, setShapeColor] = useState('#3b82f6');
  const [drawColor, setDrawColor] = useState('#000000');
  const [drawWidth, setDrawWidth] = useState(5);
  const [fontSize, setFontSize] = useState(24);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [cornerRadius, setCornerRadius] = useState(0);

  useEffect(() => {
    if (imageRef.current) {
      imageRef.current.cache();
      imageRef.current.getLayer()?.batchDraw();
    }
  }, [image]);

  // Cache base image once it loads for faster filter updates
  useEffect(() => {
    if (image && imageRef.current) {
      imageRef.current.cache();
      imageRef.current.getLayer()?.batchDraw();
    }
  }, [image]);

  // Apply corner radius to image using Group clipFunc
  useEffect(() => {
    if (groupRef.current && imageRef.current && image) {
      const group = groupRef.current;
      const img = imageRef.current;
      
      if (cornerRadius > 0) {
        group.clipFunc((ctx: CanvasRenderingContext2D) => {
          const width = img.width() * img.scaleX();
          const height = img.height() * img.scaleY();
          // Coordinates are relative to the group, which is at (0, 0)
          const x = img.x();
          const y = img.y();
          
          ctx.beginPath();
          ctx.moveTo(x + cornerRadius, y);
          ctx.lineTo(x + width - cornerRadius, y);
          ctx.quadraticCurveTo(x + width, y, x + width, y + cornerRadius);
          ctx.lineTo(x + width, y + height - cornerRadius);
          ctx.quadraticCurveTo(x + width, y + height, x + width - cornerRadius, y + height);
          ctx.lineTo(x + cornerRadius, y + height);
          ctx.quadraticCurveTo(x, y + height, x, y + height - cornerRadius);
          ctx.lineTo(x, y + cornerRadius);
          ctx.quadraticCurveTo(x, y, x + cornerRadius, y);
          ctx.closePath();
        });
      } else {
        group.clipFunc(undefined);
      }
      
      group.getLayer()?.batchDraw();
    }
  }, [cornerRadius, image, imagePosition, zoom]);

  // Calculate stage size based on image
  useEffect(() => {
    if (image) {
      const maxWidth = 900;
      const maxHeight = 700;
      const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
      setStageSize({
        width: image.width * scale,
        height: image.height * scale,
      });
      setImagePosition({ x: 0, y: 0, scale });
    }
  }, [image]);

  // Update transformer when selection changes
  useEffect(() => {
    if (!transformerRef.current) return;

    const stage = stageRef.current;
    if (!stage) return;

    const selectedNode = stage.findOne(`.${selectedId}`);
    if (selectedNode) {
      transformerRef.current.nodes([selectedNode]);
      transformerRef.current.getLayer()?.batchDraw();
    } else {
      transformerRef.current.nodes([]);
    }
  }, [selectedId]);

  const handleStageClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      setSelectedId(null);
      return;
    }

    const clickedOnTransformer = e.target.getParent()?.className === 'Transformer';
    if (clickedOnTransformer) {
      return;
    }

    const name = e.target.name();
    if (name) {
      setSelectedId(name);
    }
  };

  const handleTextClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (mode === 'text') {
      const stage = e.target.getStage();
      if (!stage) return;

      const pointerPos = stage.getPointerPosition();
      if (!pointerPos) return;

      const newText: TextElement = {
        id: `text-${Date.now()}`,
        x: pointerPos.x,
        y: pointerPos.y,
        text: 'Double click to edit',
        fontSize,
        fontFamily,
        fill: textColor,
      };
      setTextElements([...textElements, newText]);
      setSelectedId(newText.id);
    }
  };

  const handleTextDblClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const textNode = e.target as Konva.Text;
    const stage = textNode.getStage();
    if (!stage) return;

    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);

    textarea.value = textNode.text();
    textarea.style.position = 'absolute';
    textarea.style.top = `${stage.container().offsetTop + textNode.y()}px`;
    textarea.style.left = `${stage.container().offsetLeft + textNode.x()}px`;
    textarea.style.width = `${textNode.width()}px`;
    textarea.style.height = `${textNode.height()}px`;
    textarea.style.fontSize = `${textNode.fontSize()}px`;
    textarea.style.border = 'none';
    textarea.style.padding = '0px';
    textarea.style.margin = '0px';
    textarea.style.overflow = 'hidden';
    textarea.style.background = 'transparent';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.fontFamily = textNode.fontFamily();
    textarea.style.transformOrigin = 'left top';
    textarea.style.textAlign = textNode.align();
    textarea.style.color = textNode.fill();

    const rotation = textNode.rotation();
    const scaleX = textNode.scaleX();
    const scaleY = textNode.scaleY();

    textarea.style.transform = `rotate(${rotation}deg) scale(${scaleX}, ${scaleY})`;

    textarea.focus();

    const removeTextarea = () => {
      textarea.parentNode?.removeChild(textarea);
      window.removeEventListener('click', removeTextarea);
    };

    textarea.addEventListener('keydown', (e) => {
      if (e.keyCode === 13 && !e.shiftKey) {
        textNode.text(textarea.value);
        removeTextarea();
      }
      if (e.keyCode === 27) {
        removeTextarea();
      }
    });

    window.addEventListener('click', removeTextarea);
  };

  const handleDrawStart = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (mode !== 'draw') return;

    setIsDrawing(true);
    const stage = e.target.getStage();
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    setCurrentPath([pos.x, pos.y]);
  };

  const handleDrawMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!isDrawing || mode !== 'draw') return;

    const stage = e.target.getStage();
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    setCurrentPath([...currentPath, pos.x, pos.y]);
  };

  const handleDrawEnd = () => {
    if (!isDrawing) return;

    if (currentPath.length > 0) {
      const newPath: DrawPath = {
        id: `path-${Date.now()}`,
        points: currentPath,
        stroke: drawColor,
        strokeWidth: drawWidth,
      };
      setDrawPaths([...drawPaths, newPath]);
    }

    setIsDrawing(false);
    setCurrentPath([]);
  };

  const handleShapeClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (mode === 'shapes') {
      const stage = e.target.getStage();
      if (!stage) return;

      const pointerPos = stage.getPointerPosition();
      if (!pointerPos) return;

      const newShape: ShapeElement = {
        id: `shape-${Date.now()}`,
        type: 'rect',
        x: pointerPos.x,
        y: pointerPos.y,
        width: 100,
        height: 100,
        fill: shapeColor,
        stroke: shapeColor,
        strokeWidth: 2,
      };
      setShapeElements([...shapeElements, newShape]);
      setSelectedId(newShape.id);
    }
  };

  const handleSave = () => {
    const stage = stageRef.current;
    if (!stage) return;

    const dataURL = stage.toDataURL({
      pixelRatio: 2,
      quality: 1,
    });
    onSave(dataURL);
  };

  const handleDelete = () => {
    if (!selectedId) return;

    setTextElements(textElements.filter((el) => el.id !== selectedId));
    setShapeElements(shapeElements.filter((el) => el.id !== selectedId));
    setDrawPaths(drawPaths.filter((el) => el.id !== selectedId));
    setSelectedId(null);
  };

  const updateImageFilters = (activeFilters: string[]) => {
    const img = imageRef.current;
    if (!img) return;

    const appliedFilters: Konva.Filters[] = [];

    if (activeFilters.length === 0) {
      img.filters([]);
      img.blurRadius(0);
      img.brightness(0);
      img.contrast(0);
      img.cache();
      img.getLayer()?.batchDraw();
      return;
    }

    const filter = FILTER_OPTIONS.find((opt) => opt.id === activeFilters[0]);
    if (!filter) return;

    const filterMap: Record<string, Konva.Filters> = {
      blur: Konva.Filters.Blur,
      brighten: Konva.Filters.Brighten,
      contrast: Konva.Filters.Contrast,
      grayscale: Konva.Filters.Grayscale,
      invert: Konva.Filters.Invert,
      sepia: Konva.Filters.Sepia,
      sharpen: Konva.Filters.Sharpen,
      vintage: Konva.Filters.Sepia,
      polaroid: Konva.Filters.Sepia,
      kodachrome: Konva.Filters.Sepia,
      technicolor: Konva.Filters.Sepia,
      brownie: Konva.Filters.Sepia,
      comic: Konva.Filters.Contrast,
      cool: Konva.Filters.Contrast,
      warm: Konva.Filters.Contrast,
      dream: Konva.Filters.Blur,
    };

    const konvaFilter = filterMap[filter.id];
    img.filters(konvaFilter ? [konvaFilter] : []);

    img.blurRadius(0);
    img.brightness(0);
    img.contrast(0);

    if (filter.konva) {
      filter.konva(img);
    }

    img.cache();
    img.getLayer()?.batchDraw();
  };

  const applyFilter = (filterName: string) => {
    const newFilters = filters.includes(filterName) ? [] : [filterName];
    setFilters(newFilters);
    updateImageFilters(newFilters);
  };

  const zoomFactor = zoom / 100;

  const adjustZoom = (delta: number) => {
    setZoom((prev) => Math.min(200, Math.max(10, prev + delta)));
  };

  const isFilterMode = mode === 'filter';
  const isCornersMode = mode === 'corners';
  const shouldHideBottomToolbar = isFilterMode || isCornersMode;

  const renderModeControls = () => {
    const filterRibbon = (
      <div className="h-28 bg-[#1a1a1a] border-t border-[#3a3a3a] px-6 py-4 flex items-center">
        <div className="flex items-center gap-4 w-full overflow-x-auto no-scrollbar">
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.id}
              onClick={() => applyFilter(option.id)}
              className={`flex-shrink-0 flex items-center gap-2 px-2 py-1.5 transition-all ${
                filters.includes(option.id) ? 'text-white drop-shadow' : 'text-white/70 hover:text-white'
              }`}
            >
              <div className="relative w-20 h-12 overflow-hidden shadow-lg shadow-black/40">
                <img
                  src={image ? image.src : DEFAULT_PREVIEW_IMAGE}
                  alt={option.label}
                  className="w-full h-full object-cover"
                  style={{ filter: option.cssFilter }}
                  loading="lazy"
                />
              </div>
              <span className="text-xs font-medium whitespace-nowrap">{option.label}</span>
            </button>
          ))}
        </div>
      </div>
    );

    if (isFilterMode) {
      return filterRibbon;
    }

    const baseClass =
      'h-28 bg-[#1a1a1a] border-t border-[#3a3a3a] px-6 py-4 flex items-center gap-6 overflow-x-auto';

    switch (mode) {
      case 'resize':
        return null;
      case 'draw':
        return (
          <div className={baseClass}>
            <div className="flex items-center gap-6 text-sm text-white">
              <label className="flex items-center gap-2">
                <span>Color</span>
                <input
                  type="color"
                  value={drawColor}
                  onChange={(e) => setDrawColor(e.target.value)}
                  className="w-10 h-10 rounded border-0 bg-transparent"
                />
              </label>
              <label className="flex items-center gap-2">
                <span>Brush</span>
                <input
                  type="range"
                  min={1}
                  max={50}
                  value={drawWidth}
                  onChange={(e) => setDrawWidth(Number(e.target.value))}
                />
                <span>{drawWidth}px</span>
              </label>
            </div>
          </div>
        );
      case 'text':
        return (
          <div className={baseClass}>
            <div className="flex items-center gap-4 text-sm text-white">
              <label className="flex items-center gap-2">
                <span>Font</span>
                <select
                  value={fontFamily}
                  onChange={(e) => setFontFamily(e.target.value)}
                  className="bg-[#2a2a2a] border border-[#3a3a3a] rounded px-2 py-1"
                >
                  {['Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Verdana'].map(
                    (font) => (
                      <option key={font} value={font}>
                        {font}
                      </option>
                    )
                  )}
                </select>
              </label>
              <label className="flex items-center gap-2">
                <span>Size</span>
                <input
                  type="number"
                  min={8}
                  max={200}
                  value={fontSize}
                  onChange={(e) => setFontSize(Number(e.target.value))}
                  className="w-20 bg-[#2a2a2a] border border-[#3a3a3a] rounded px-2 py-1"
                />
              </label>
              <label className="flex items-center gap-2">
                <span>Color</span>
                <input
                  type="color"
                  value={textColor}
                  onChange={(e) => setTextColor(e.target.value)}
                  className="w-10 h-10 rounded border-0 bg-transparent"
                />
              </label>
            </div>
          </div>
        );
      case 'shapes':
        return (
          <div className={baseClass}>
            <div className="flex items-center gap-4 text-sm text-white">
              <span>Fill Color</span>
              <input
                type="color"
                value={shapeColor}
                onChange={(e) => setShapeColor(e.target.value)}
                className="w-10 h-10 rounded border-0 bg-transparent"
              />
            </div>
          </div>
        );
      case 'corners':
        return (
          <div className="h-28 bg-[#1a1a1a] border-t border-[#3a3a3a] px-6 py-4 flex items-center justify-center">
            <div className="w-full max-w-2xl flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Radius</span>
                <span className="text-white text-sm font-medium">{cornerRadius}px</span>
              </div>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={0}
                  max={150}
                  value={cornerRadius}
                  onChange={(e) => setCornerRadius(Number(e.target.value))}
                  className="flex-1 h-1.5 rounded-full appearance-none bg-transparent focus:outline-none cursor-pointer slider-thumb-green"
                  style={{
                    background: `linear-gradient(to right, #22c55e 0%, #22c55e ${(cornerRadius / 150) * 100}%, #ffffff ${(cornerRadius / 150) * 100}%, #ffffff 100%)`,
                    accentColor: '#22c55e',
                  }}
                />
              </div>
            </div>
          </div>
        );
      case 'stickers':
      case 'frame':
      case 'merge':
      case 'crop':
        return (
          <div className={baseClass}>
            <p className="text-white/70 text-sm">
              {mode.charAt(0).toUpperCase() + mode.slice(1)} tools coming soon.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        input[type="range"].slider-thumb-green::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #22c55e;
          cursor: pointer;
          border: none;
        }
        input[type="range"].slider-thumb-green::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #22c55e;
          cursor: pointer;
          border: none;
        }
      `}</style>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="w-full max-w-[95vw] h-full max-h-[98vh] bg-[#2a2a2a] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#3a3a3a] bg-[#1a1a1a]">
          {isFilterMode || isCornersMode ? (
            <div className="flex items-center">
              <button
                onClick={() => {
                  if (isFilterMode) {
                    setMode('none');
                    setFilters([]);
                    updateImageFilters([]);
                  } else if (isCornersMode) {
                    setMode('none');
                  }
                }}
                className="px-4 py-2 rounded-lg border border-[#3a3a3a] text-sm font-semibold text-white/80 hover:text-white hover:bg-[#3a3a3a] transition-all"
              >
                Close
              </button>
            </div>
          ) : (
            <div>
              <h2 className="text-lg font-semibold text-white">Image Editor</h2>
              <p className="text-sm text-white/60">Edit your image with powerful tools</p>
            </div>
          )}
          <div className="flex items-center gap-3">
            {isFilterMode || isCornersMode ? (
              <>
                <button
                  onClick={() => {
                    if (isFilterMode) {
                      setMode('none');
                      setFilters([]);
                      updateImageFilters([]);
                    } else if (isCornersMode) {
                      handleSave();
                    }
                  }}
                  className="px-5 py-2.5 rounded-lg bg-green-500 text-white text-sm font-semibold hover:bg-green-600 transition-all disabled:opacity-60"
                  disabled={isSaving || !image}
                >
                  {isSaving ? 'Saving...' : 'Apply'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg border border-[#3a3a3a] text-sm font-medium text-white/70 hover:text-white hover:bg-[#3a3a3a] transition-all"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-all disabled:opacity-60"
                  disabled={isSaving || !image}
                >
                  {isSaving ? 'Saving...' : 'Apply Changes'}
                </button>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-[#3a3a3a] transition-all"
                  disabled={isSaving}
                  aria-label="Close editor"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden bg-[#2a2a2a]">
          {/* Canvas Area */}
          <div className="flex-1 flex items-center justify-center p-4 overflow-hidden relative bg-[#2a2a2a]">
            {/* Zoom Controls at Top */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3 bg-[#1a1a1a]/90 backdrop-blur-sm px-4 py-2 rounded-lg border border-[#3a3a3a]">
              <button
                className="text-white hover:text-blue-400 transition-colors"
                onClick={() => adjustZoom(-10)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <span className="text-white text-sm font-medium">{zoom}%</span>
              <button
                className="text-white hover:text-blue-400 transition-colors"
                onClick={() => adjustZoom(10)}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
            {image ? (
              <div className="max-w-full max-h-full overflow-hidden">
                {/* @ts-ignore - react-konva Stage accepts children but types are incorrect */}
                <Stage
                  ref={stageRef as React.RefObject<Konva.Stage>}
                  width={stageSize.width}
                  height={stageSize.height}
                  onMouseDown={(e: Konva.KonvaEventObject<MouseEvent>) => {
                    if (mode === 'draw') {
                      handleDrawStart(e);
                    } else if (mode === 'text') {
                      handleTextClick(e);
                    } else if (mode === 'shapes') {
                      handleShapeClick(e);
                    } else {
                      handleStageClick(e);
                    }
                  }}
                  onMouseMove={mode === 'draw' ? handleDrawMove : undefined}
                  onMouseUp={mode === 'draw' ? handleDrawEnd : undefined}
                  onTouchStart={(e: Konva.KonvaEventObject<TouchEvent>) => {
                    if (mode !== 'draw' && mode !== 'text' && mode !== 'shapes') {
                      handleStageClick(e);
                    }
                  }}
                >
                  <Layer>
                    <Group ref={groupRef}>
                      <KonvaImage
                        ref={imageRef}
                        image={image}
                        x={imagePosition.x}
                        y={imagePosition.y}
                        scaleX={imagePosition.scale * zoomFactor}
                        scaleY={imagePosition.scale * zoomFactor}
                        filters={filters.map((f) => {
                          switch (f) {
                            case 'blur':
                              return Konva.Filters.Blur;
                            case 'brighten':
                              return Konva.Filters.Brighten;
                            case 'contrast':
                              return Konva.Filters.Contrast;
                            case 'grayscale':
                              return Konva.Filters.Grayscale;
                            case 'invert':
                              return Konva.Filters.Invert;
                            case 'sepia':
                              return Konva.Filters.Sepia;
                            case 'sharpen':
                              return Konva.Filters.Sharpen;
                            default:
                              return null;
                          }
                        }).filter(Boolean) as any}
                        cache
                      />
                    </Group>

                    {/* Draw Paths */}
                    {drawPaths.map((path) => (
                      <Line
                        key={path.id}
                        points={path.points}
                        stroke={path.stroke}
                        strokeWidth={path.strokeWidth}
                        tension={0.5}
                        lineCap="round"
                        lineJoin="round"
                        globalCompositeOperation="source-over"
                      />
                    ))}

                    {/* Current Drawing Path */}
                    {isDrawing && currentPath.length > 0 && (
                      <Line
                        points={currentPath}
                        stroke={drawColor}
                        strokeWidth={drawWidth}
                        tension={0.5}
                        lineCap="round"
                        lineJoin="round"
                        globalCompositeOperation="source-over"
                      />
                    )}

                    {/* Shapes */}
                    {shapeElements.map((shape) => {
                      if (shape.type === 'rect') {
                        return (
                          <Rect
                            key={shape.id}
                            name={shape.id}
                            x={shape.x}
                            y={shape.y}
                            width={shape.width}
                            height={shape.height}
                            fill={shape.fill}
                            stroke={shape.stroke}
                            strokeWidth={shape.strokeWidth}
                            draggable
                            onClick={() => setSelectedId(shape.id)}
                          />
                        );
                      } else if (shape.type === 'circle') {
                        return (
                          <Circle
                            key={shape.id}
                            name={shape.id}
                            x={shape.x + shape.width / 2}
                            y={shape.y + shape.height / 2}
                            radius={Math.min(shape.width, shape.height) / 2}
                            fill={shape.fill}
                            stroke={shape.stroke}
                            strokeWidth={shape.strokeWidth}
                            draggable
                            onClick={() => setSelectedId(shape.id)}
                          />
                        );
                      }
                      return null;
                    })}

                    {/* Text Elements */}
                    {textElements.map((textEl) => (
                      <Text
                        key={textEl.id}
                        name={textEl.id}
                        x={textEl.x}
                        y={textEl.y}
                        text={textEl.text}
                        fontSize={textEl.fontSize}
                        fontFamily={textEl.fontFamily}
                        fill={textEl.fill}
                        fontStyle={textEl.fontStyle}
                        fontWeight={textEl.fontWeight}
                        draggable
                        onClick={() => setSelectedId(textEl.id)}
                        onDblClick={handleTextDblClick}
                      />
                    ))}

                    {/* Transformer */}
                    <Transformer
                      ref={transformerRef}
                      boundBoxFunc={(oldBox: any, newBox: any) => {
                        if (Math.abs(newBox.width) < 5 || Math.abs(newBox.height) < 5) {
                          return oldBox;
                        }
                        return newBox;
                      }}
                    />
                  </Layer>
                </Stage>
              </div>
            ) : (
              <div className="text-center text-white/60">
                <p>Loading image...</p>
              </div>
            )}
          </div>

          {renderModeControls()}

          {/* Bottom Toolbar */}
          {!shouldHideBottomToolbar && (
          <div className="h-28 bg-[#1a1a1a] border-t border-[#3a3a3a] flex items-center justify-center gap-8 px-6">
            <ToolButton
              icon="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
              label="Filter"
              active={isFilterMode}
              onClick={() => setMode('filter')}
            />
            <ToolButton
              icon="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
              label="Resize"
              active={mode === 'resize'}
              onClick={() => setMode('resize')}
            />
            <ToolButton
              icon="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              label="Crop"
              active={mode === 'crop'}
              onClick={() => setMode('crop')}
            />
            <ToolButton
              icon="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              label="Draw"
              active={mode === 'draw'}
              onClick={() => setMode('draw')}
            />
            <ToolButton
              icon="M4 6h16M4 12h16M4 18h7"
              label="Text"
              active={mode === 'text'}
              onClick={() => setMode('text')}
            />
            <ToolButton
              icon="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
              label="Shapes"
              active={mode === 'shapes'}
              onClick={() => setMode('shapes')}
            />
            <ToolButton
              icon="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              label="Stickers"
              active={mode === 'stickers'}
              onClick={() => setMode('stickers')}
            />
            <ToolButton
              icon="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              label="Frame"
              active={mode === 'frame'}
              onClick={() => setMode('frame')}
            />
            <ToolButton
              icon="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6z"
              label="Corners"
              active={(mode as EditorMode) === 'corners'}
              onClick={() => setMode('corners')}
            />
            <ToolButton
              icon="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
              label="Merge"
              active={mode === 'merge'}
              onClick={() => setMode('merge')}
            />
          </div>
          )}
        </div>

      </div>
    </div>
    </>
  );
};

interface ToolButtonProps {
  icon: string;
  label: string;
  active: boolean;
  onClick: () => void;
}

const ToolButton: React.FC<ToolButtonProps> = ({ icon, label, active, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`
        relative flex flex-col items-center justify-center gap-1.5 px-4 py-2
        transition-all duration-200 ease-in-out
        group
        ${
          active
            ? 'text-white'
            : 'text-white/70 hover:text-white'
        }
      `}
      title={label}
    >
      <div className={`
        flex items-center justify-center transition-transform duration-200
        ${active ? 'scale-110' : 'group-hover:scale-110'}
      `}>
        <svg 
          className={`w-6 h-6 transition-all duration-200`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          strokeWidth={active ? 2.5 : 2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
        </svg>
      </div>
      
      <span className={`
        text-xs font-medium transition-all duration-200
        ${active ? 'text-white' : 'text-white/70 group-hover:text-white'}
      `}>
        {label}
      </span>
      
      {/* Active indicator */}
      {active && (
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-white rounded-full" />
      )}
    </button>
  );
};

export default KonvaImageEditor;

